/**
 * Tarih ve saat seçici.
 *
 * Ürün Sahibi, 21.09.2026 CRM incelemesi: "böyle bir date picker'ı kabul etmiyorum, zaten var
 * v3'te kullandığımız asıl ve modern olan datepicker." Kastedilen, v3'ün arama kutusundaki
 * takvim: Cally (`calendar-date`, `calendar-month`), v3 `package.json` içinde `cally@^0.9.2`.
 * Aynı sürüm buraya da alındı, çünkü iki üründe iki ayrı takvim iki ayrı klavye davranışı ve
 * iki ayrı hata yüzeyi demek.
 *
 * Yerine geçtiği şey `<input type="datetime-local">` idi: tarayıcının kendi paneli, İngilizce ay
 * adları, `dd.mm.yyyy, --:--` biçim ipucu ve mavi sistem renkleri. Marka içinde duran hiçbir
 * yanı yoktu.
 *
 * Cally bir web bileşeni, React bileşeni değil. İki sonucu var ve ikisi de burada ele alınıyor:
 * değer **özellik** olarak yazılıyor (`setAttribute`), ve `change` olayı React'in sentetik olay
 * sistemine düşmediği için `addEventListener` ile dinleniyor.
 */
import { useEffect, useId, useRef, useState } from 'react';
import 'cally';
import './tarih-secici.css';
import { ORTAK } from './metin/ortak';
import { geceFarki } from './hotel-offer-utils';

const AYLAR = ['Ocak', 'Şubat', 'Mart', 'Nisan', 'Mayıs', 'Haziran', 'Temmuz', 'Ağustos', 'Eylül', 'Ekim', 'Kasım', 'Aralık'];

/** `2026-10-03T14:45` -> `{ gun: '2026-10-03', saat: '14:45' }`; bozuk girdi boş döner. */
export function parcala(deger: string): { gun: string; saat: string } {
  const eslesme = /^(\d{4}-\d{2}-\d{2})(?:T(\d{2}:\d{2}))?/u.exec(deger.trim());
  if (!eslesme) return { gun: '', saat: '' };
  return { gun: eslesme[1] ?? '', saat: eslesme[2] ?? '' };
}

/** Ekranda okunan biçim: `3 Ekim 2026, 14:45`. Saat yoksa yalnız gün yazılır. */
export function okunurTarih(deger: string): string {
  const { gun, saat } = parcala(deger);
  if (gun === '') return '';
  const [yil = '', ay = '01', gunSayisi = '1'] = gun.split('-');
  const metin = `${Number.parseInt(gunSayisi, 10)} ${AYLAR[Number.parseInt(ay, 10) - 1] ?? ''} ${yil}`;
  return saat === '' ? metin : `${metin}, ${saat}`;
}

export function TarihSaatSecici({
  id: fieldId,
  'aria-describedby': describedBy,
  deger,
  setDeger,
  testId,
  saatli = true,
}: {
  id?: string;
  'aria-describedby'?: string;
  deger: string;
  setDeger: (yeni: string) => void;
  testId: string;
  saatli?: boolean;
}) {
  const [acik, setAcik] = useState(false);
  const takvimRef = useRef<HTMLElement | null>(null);
  const kutuRef = useRef<HTMLDivElement | null>(null);
  const id = useId();
  const { gun, saat } = parcala(deger);

  /**
   * Cally'nin `change` olayı. React'in `onChange`i özel elementlerde çalışmıyor, o yüzden elle
   * bağlanıyor. Saat seçilmemişse gün seçimi 09:00 ile tamamlanıyor: saatsiz bir `nextActionAt`
   * uçta geçersiz ISO damgası oluyor (`@IsISO8601`).
   */
  useEffect(() => {
    const element = takvimRef.current;
    if (!element) return undefined;
    const dinleyici = (olay: Event) => {
      const secilen = (olay.target as HTMLElement & { value?: string }).value ?? '';
      if (secilen === '') return;
      setDeger(saatli ? `${secilen}T${saat === '' ? '09:00' : saat}` : secilen);
    };
    element.addEventListener('change', dinleyici);
    return () => element.removeEventListener('change', dinleyici);
  }, [setDeger, saat, saatli, acik]);

  /** Değer dışarıdan değiştiğinde takvim onu göstersin. */
  useEffect(() => {
    const element = takvimRef.current;
    if (!element) return;
    if (gun === '') element.removeAttribute('value');
    else element.setAttribute('value', gun);
  }, [gun, acik]);

  /** Panelin dışına tıklamak kapatır; kaçış tuşu da kapatır. */
  useEffect(() => {
    if (!acik) return undefined;
    const disTiklama = (olay: MouseEvent) => {
      if (kutuRef.current && !kutuRef.current.contains(olay.target as Node)) setAcik(false);
    };
    const kacis = (olay: KeyboardEvent) => {
      if (olay.key === 'Escape') setAcik(false);
    };
    document.addEventListener('mousedown', disTiklama);
    document.addEventListener('keydown', kacis);
    return () => {
      document.removeEventListener('mousedown', disTiklama);
      document.removeEventListener('keydown', kacis);
    };
  }, [acik]);

  return (
    <div className="tarih-secici" ref={kutuRef}>
      <button
        type="button"
        className="tarih-secici-alan"
        id={fieldId ?? id}
        aria-describedby={describedBy}
        data-testid={testId}
        aria-haspopup="dialog"
        aria-expanded={acik}
        onClick={() => setAcik((onceki) => !onceki)}
      >
        <span className={deger === '' ? 'tarih-secici-bos' : undefined} data-testid={`${testId}-metin`}>
          {deger === '' ? ORTAK.tarihSecin : okunurTarih(deger)}
        </span>
        <span aria-hidden="true" className="tarih-secici-simge">▾</span>
      </button>
      {acik ? (
        <div className="tarih-secici-panel" role="dialog" aria-label={ORTAK.tarihSecin} data-testid={`${testId}-panel`}>
          <TakvimKutusu takvimRef={takvimRef} />
          {saatli ? (
            <label className="tarih-secici-saat">
              {ORTAK.saat}
              <input
                type="time"
                data-testid={`${testId}-saat`}
                value={saat}
                onChange={(olay) => {
                  const yeniSaat = olay.target.value;
                  if (gun === '') return;
                  setDeger(yeniSaat === '' ? gun : `${gun}T${yeniSaat}`);
                }}
              />
            </label>
          ) : null}
          <div className="tarih-secici-eylemler">
            <button type="button" data-testid={`${testId}-temizle`} onClick={() => { setDeger(''); setAcik(false); }}>
              {ORTAK.temizle}
            </button>
            <button type="button" data-testid={`${testId}-kapat`} onClick={() => setAcik(false)}>
              {ORTAK.tamam}
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
}

/**
 * Cally elementleri tek bir yerde çiziliyor. JSX bu etiketleri tanımadığı için tip kaçışı burada
 * kalıyor ve bileşenin geri kalanı tip denetiminde duruyor.
 */
function TakvimKutusu({ takvimRef }: { takvimRef: React.MutableRefObject<HTMLElement | null> }) {
  const CalendarDate = 'calendar-date' as unknown as React.ElementType;
  const CalendarMonth = 'calendar-month' as unknown as React.ElementType;
  return (
    <CalendarDate ref={takvimRef} locale="tr-TR" first-day-of-week="1">
      <AyOklari />
      <CalendarMonth />
    </CalendarDate>
  );
}

/**
 * Cally'nin gezinme düğmeleri varsayılan olarak İngilizce ("Previous", "Next") ve başlıkta
 * yalnız yıl yazıyordu (Ürün Sahibi ekran görüntüsü, 03.10.2026). Oklar yuvalara bizden gidiyor;
 * düğmenin erişilebilir adı yuvadaki `aria-label`dan geliyor. Başlık yuvası boş: ay adı
 * `calendar-month`un kendi başlığında. Aynı desen v3'ün arama kutusunda (`src/ui/search.ts`).
 */
function AyOklari() {
  const ok = (yon: 'sol' | 'sag') => (
    <svg aria-hidden="true" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d={yon === 'sol' ? 'm15 18-6-6 6-6' : 'm9 18 6-6-6-6'} />
    </svg>
  );
  return (
    <>
      <span slot="previous" aria-label={ORTAK.oncekiAy}>{ok('sol')}</span>
      <span slot="next" aria-label={ORTAK.sonrakiAy}>{ok('sag')}</span>
      <span slot="heading" />
    </>
  );
}

/**
 * Giriş ve çıkış tek alanda (Ürün Sahibi, 03.10.2026: "giriş çıkış tek date input olsun").
 * Cally `calendar-range`: ilk tıklama girişi, ikinci tıklama çıkışı seçer; `change` olayı aralık
 * tamamlanınca gelir ve değer `YYYY-MM-DD/YYYY-MM-DD` biçimindedir. Aralık tamamlanınca panel
 * kapanır. Aynı bileşen v3'ün arama kutusundaki aralık takvimi.
 */
export function TarihAraligiSecici({
  id: fieldId,
  testId,
  giris,
  cikis,
  setAralik,
  bosMetin,
  yarimMetin,
  geceGoster = true,
}: {
  id?: string;
  testId: string;
  giris: string;
  cikis: string;
  setAralik: (giris: string, cikis: string) => void;
  /** Boş alanın metni. Verilmezse konaklama metni (giriş ve çıkış). Süzgeç kullanımları kendi metnini verir. */
  bosMetin?: string;
  yarimMetin?: string;
  /** `false`: aralığın yanına "N gece" yazılmaz (konaklama dışı kullanım, örneğin liste süzgeci). */
  geceGoster?: boolean;
}) {
  const [acik, setAcik] = useState(false);
  const [yarim, setYarim] = useState(false);
  const takvimRef = useRef<HTMLElement | null>(null);
  const kutuRef = useRef<HTMLDivElement | null>(null);
  const id = useId();
  const gece = geceGoster && giris && cikis ? geceFarki(giris, cikis) : null;

  useEffect(() => {
    const element = takvimRef.current;
    if (!element) return undefined;
    const degisti = (olay: Event) => {
      const [bas = '', son = ''] = ((olay.target as HTMLElement & { value?: string }).value ?? '').split('/');
      if (!/^\d{4}-\d{2}-\d{2}$/.test(bas) || !/^\d{4}-\d{2}-\d{2}$/.test(son)) return;
      setYarim(false);
      setAralik(bas, son);
      setAcik(false);
    };
    const basladi = () => setYarim(true);
    element.addEventListener('change', degisti);
    element.addEventListener('rangestart', basladi);
    return () => {
      element.removeEventListener('change', degisti);
      element.removeEventListener('rangestart', basladi);
    };
  }, [setAralik, acik]);

  useEffect(() => {
    const element = takvimRef.current;
    if (!element) return;
    if (giris && cikis) element.setAttribute('value', `${giris}/${cikis}`);
    else element.removeAttribute('value');
  }, [giris, cikis, acik]);

  useEffect(() => {
    if (!acik) return undefined;
    const disTiklama = (olay: MouseEvent) => {
      if (kutuRef.current && !kutuRef.current.contains(olay.target as Node)) setAcik(false);
    };
    const kacis = (olay: KeyboardEvent) => {
      if (olay.key === 'Escape') setAcik(false);
    };
    document.addEventListener('mousedown', disTiklama);
    document.addEventListener('keydown', kacis);
    return () => {
      document.removeEventListener('mousedown', disTiklama);
      document.removeEventListener('keydown', kacis);
    };
  }, [acik]);

  // `months` ve `offset` Cally'de **sayı** özellikleri. React 19 özel elementlerde özelliği doğrudan
  // yazıyor, yani `months="2"` (dize) Cally'nin tür denetimine takılıp "must be of type Number"
  // ile bütün bölümü hata sınırına düşürüyordu (06.10.2026, e2e ile ölçüldü).
  const CalendarRange = 'calendar-range' as unknown as React.ElementType;
  const CalendarMonth = 'calendar-month' as unknown as React.ElementType;
  const bos = !giris || !cikis;
  return (
    <div className="tarih-secici" ref={kutuRef}>
      <button
        type="button"
        className="tarih-secici-alan"
        id={fieldId ?? id}
        data-testid={testId}
        aria-haspopup="dialog"
        aria-expanded={acik}
        onClick={() => { setYarim(false); setAcik((onceki) => !onceki); }}
      >
        <span className={bos ? 'tarih-secici-bos' : undefined} data-testid={`${testId}-metin`}>
          {bos ? (bosMetin ?? ORTAK.tarihAraligiSecin) : `${okunurTarih(giris)} – ${okunurTarih(cikis)}${gece === null ? '' : ` · ${ORTAK.geceSayisi(gece)}`}`}
        </span>
        <span aria-hidden="true" className="tarih-secici-simge">▾</span>
      </button>
      {acik ? (
        <div className="tarih-secici-panel tarih-araligi-panel" role="dialog" aria-label={bosMetin ?? ORTAK.tarihAraligiSecin} data-testid={`${testId}-panel`}>
          <CalendarRange ref={takvimRef} locale="tr-TR" first-day-of-week="1" months={2}>
            <AyOklari />
            <div className="tarih-araligi-aylar">
              <CalendarMonth />
              <CalendarMonth offset={1} />
            </div>
          </CalendarRange>
          {yarim ? <p className="muted" role="status" data-testid={`${testId}-yarim`}>{yarimMetin ?? ORTAK.cikisSecin}</p> : null}
          <div className="tarih-secici-eylemler">
            <button type="button" data-testid={`${testId}-temizle`} onClick={() => { setAralik('', ''); setAcik(false); }}>
              {ORTAK.temizle}
            </button>
            <button type="button" data-testid={`${testId}-kapat`} onClick={() => setAcik(false)}>
              {ORTAK.tamam}
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
}
